<template>
  <Teleport to="body">
    <div v-if="isVisible" :class="$style.modalOverlay" @click="closeModal" @touchmove.prevent @wheel.prevent>
      <div :class="$style.modalContent" @click.stop>
        <div :class="$style.modalHeader">
          <h2 :class="$style.modalTitle">프로필 사진 편집</h2>
          <button :class="$style.closeButton" @click="closeModal">✕</button>
        </div>

        <div :class="$style.modalBody">
          <!-- 현재 프로필 이미지 미리보기 -->
          <div :class="$style.imagePreviewContainer">
            <div :class="$style.imagePreview">
              <button :class="$style.imagePreviewButton" @click="triggerFileSelect" :disabled="uploading">
                <img v-if="currentImage || previewImage" :src="previewImage || currentImage" :alt="displayName"
                  :class="$style.previewImage" />
                <div v-else :class="$style.noImage">
                  <img :src="require('@/assets/images/default_profile.png')" :alt="displayName"
                    :class="$style.previewImage" />
                </div>
              </button>
            </div>
            <p :class="$style.imageLabel">{{ displayName }}</p>
          </div>

          <!-- 파일 선택 영역 -->
          <div :class="$style.uploadSection">
            <input ref="fileInput" type="file" accept="image/*" @change="handleFileSelect"
              :class="$style.hiddenInput" />

            <!-- <button
              :class="$style.selectButton"
              @click="triggerFileSelect"
              :disabled="uploading"
            >
              📷 사진 선택
            </button> -->

            <p :class="$style.uploadHint">JPG, PNG 파일만 가능 (최대 5MB)</p>
          </div>

          <!-- 업로드 진행 상태 -->
          <div v-if="uploading" :class="$style.uploadProgress">
            <div :class="$style.spinner"></div>
            <p></p>
          </div>
        </div>

        <div :class="$style.modalFooter">
          <!-- <button
            :class="$style.cancelButton"
            @click="closeModal"
            :disabled="uploading"
          >
            취소
          </button> -->
          <button v-if="currentImage && !selectedFile" :class="$style.deleteButton" @click="handleDelete"
            :disabled="uploading">
            삭제
          </button>
          <button :class="$style.saveButton" @click="handleUpload" :disabled="uploading || !selectedFile">
            저장
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
/**
 * ProfileImageModal 컴포넌트
 * 
 * 프로필 이미지 업로드/삭제를 처리하는 모달 컴포넌트입니다.
 * 
 * 모드별 동작:
 * 1. user 모드
 *    - 사용자 프로필 이미지 변경
 *    - setUserProfileImage() / removeUserProfileImage() 사용
 *    - Storage 업로드 + Firestore 업데이트를 API가 모두 처리
 * 
 * 2. update-artist / new-artist 모드
 *    - 아티스트 프로필 이미지 업로드 (ArtistForm의 수정/신규 모드)
 *    - uploadProfileImage() 사용 (Storage에만 업로드)
 *    - Firestore 저장은 ArtistForm의 폼 제출 시 처리
 *    - 폼 취소 시 업로드된 이미지는 ArtistForm에서 삭제 처리
 */

// Storage 관련: Storage에만 업로드 (update-artist/new-artist 모드)
import { uploadProfileImage, deleteImage } from "@/utils/write/storage";
// 사용자 프로필 이미지: Storage + Firestore 모두 처리
import {
  setProfileImage as setUserProfileImage,
  removeProfileImage as removeUserProfileImage,
} from "@/utils/write/user";

export default {
  name: "ProfileImageModal",
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    currentImage: {
      type: String,
      default: null,
    },
    displayName: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      default: "update-artist", // "user", "new-artist", 또는 "update-artist"
      validator: (value) =>
        ["user", "new-artist", "update-artist"].includes(value),
    },
    userEmail: {
      type: String,
      default: null, // user 모드일 때 사용
    },
  },
  emits: ["close", "image-uploaded", "image-removed"],
  data() {
    return {
      selectedFile: null,
      previewImage: null,
      uploading: false,
    };
  },
  computed: {
    /**
     * 고유 식별자 반환
     * - user 모드: userEmail 사용
     * - update-artist/new-artist 모드: displayName (아티스트명) 사용
     */
    uniqueId() {
      return this.mode === "user" ? this.userEmail : this.displayName;
    },
    /**
     * Storage 경로 반환
     * - user 모드: "profile"
     * - update-artist/new-artist 모드: "artist"
     */
    storagePath() {
      return this.mode === "user" ? "profile" : "artist";
    },
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        // 모달이 열릴 때 body 스크롤 막기
        document.body.style.overflow = "hidden";
      } else {
        // 모달이 닫힐 때 body 스크롤 복원
        document.body.style.overflow = "";
        this.resetModal();
      }
    },
  },
  beforeUnmount() {
    // 컴포넌트가 언마운트될 때 body 스크롤 복원
    document.body.style.overflow = "";
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },

    resetModal() {
      this.selectedFile = null;
      this.previewImage = null;
      this.uploading = false;
      // 파일 입력 초기화
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    triggerFileSelect() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 이미지 파일인지 확인
      if (!file.type.startsWith("image/")) {
        this.$alert("이미지 파일만 업로드 가능합니다.");
        return;
      }

      this.selectedFile = file;

      // 미리보기 이미지 생성
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    /**
     * 프로필 이미지 업로드 처리
     * 모드별로 다른 업로드 로직을 사용:
     * - user: 사용자 프로필 이미지 업로드 (API가 Storage + Firestore 모두 처리)
     * - artist/update-artist: 아티스트 프로필 이미지 업로드 (API가 Storage + Firestore 모두 처리)
     * - new-artist: 신규 아티스트 등록 시 임시 저장 (Storage에만 업로드, Firestore는 폼 제출 시 저장)
     */
    async handleUpload() {
      if (!this.selectedFile) return;

      try {
        this.uploading = true;
        let downloadURL;

        // ============================================
        // 모드별 업로드 로직 분기
        // ============================================

        if (this.mode === "user") {
          // 사용자 프로필 이미지 업로드
          // setUserProfileImage가 Storage 업로드 + Firestore 업데이트를 모두 처리
          await setUserProfileImage(this.uniqueId, this.selectedFile);
          // API 응답에 URL이 없으므로 미리보기 이미지를 사용
          downloadURL = this.previewImage;
        }
        else if (this.mode === "update-artist" || this.mode === "new-artist") {
          // 아티스트 프로필 이미지 업로드 (ArtistForm의 수정/신규 모드)
          // Storage에만 업로드하고, Firestore 저장은 ArtistForm의 폼 제출 시 처리
          // 폼 취소 시 업로드된 이미지는 ArtistForm에서 삭제 처리
          const result = await uploadProfileImage(
            this.selectedFile,
            this.uniqueId,
            "artist"
          );
          downloadURL = result.downloadURL;

          // fileName도 함께 전달 (취소 시 삭제용)
          if (result.fileName) {
            // 객체 형태로 전달하여 파일명도 함께 전달
            const imageData = {
              url: result.downloadURL,
              fileName: result.fileName
            };
            this.$emit("image-uploaded", imageData);
            // 모달 닫기 (이벤트를 이미 emit했으므로)
            this.closeModal();
            return;
          }
        }

        // 부모 컴포넌트에 업데이트 알림
        this.$emit("image-uploaded", downloadURL);

        // 모달 닫기
        this.closeModal();
      } catch (error) {
        console.error(`[${this.mode}] 프로필 사진 업로드 오류:`, error);
        this.$alert("프로필 사진 업로드 중 오류가 발생했습니다.");
      } finally {
        this.uploading = false;
      }
    },

    /**
     * 프로필 이미지 삭제 처리
     * 모드별로 다른 삭제 로직을 사용:
     * - user: 사용자 프로필 이미지 삭제 (Storage + Firestore)
     * - new-artist: 업로드 파일 삭제 + 화면에서 제거 (문서는 저장 시 공백 처리)
     * - update-artist: 화면에서만 제거 (파일은 그대로, 문서는 저장 시 공백 처리)
     */
    async handleDelete() {
      if (!this.currentImage) return;

      const confirmed = await this.$confirm("프로필 사진을 삭제하시겠습니까?");
      if (!confirmed) {
        return;
      }

      try {
        this.uploading = true;

        // ============================================
        // 모드별 삭제 로직 분기
        // ============================================

        if (this.mode === "user") {
          // 사용자 프로필 이미지 삭제: Storage + Firestore 모두 삭제
          // Firebase Storage URL에서 파일명 추출
          let fileName = null;
          try {
            const urlParts = this.currentImage.split('/o/')[1];
            if (urlParts) {
              const encodedPath = urlParts.split('?')[0];
              const fullPath = decodeURIComponent(encodedPath);
              const pathParts = fullPath.split('/');
              if (pathParts.length >= 2) {
                fileName = pathParts[pathParts.length - 1];
              }
            }
          } catch (e) {
            console.error("URL에서 파일명 추출 실패:", e);
          }

          // Storage에서 파일 삭제 시도
          if (fileName) {
            try {
              await deleteImage(this.storagePath, fileName);
            } catch (storageError) {
              console.error("Storage 파일 삭제 오류 (계속 진행):", storageError);
            }
          }

          // Firestore에서 이미지 정보 삭제
          await removeUserProfileImage(this.uniqueId);
        }
        else if (this.mode === "new-artist") {
          // 신규 등록 모드: 업로드한 파일 삭제 + 화면에서 제거
          // 파일명 추출
          let fileName = null;
          try {
            const urlParts = this.currentImage.split('/o/')[1];
            if (urlParts) {
              const encodedPath = urlParts.split('?')[0];
              const fullPath = decodeURIComponent(encodedPath);
              const pathParts = fullPath.split('/');
              if (pathParts.length >= 2) {
                fileName = pathParts[pathParts.length - 1];
              }
            }
          } catch (e) {
            console.error("URL에서 파일명 추출 실패:", e);
          }

          // Storage에서 파일 삭제
          if (fileName) {
            try {
              await deleteImage(this.storagePath, fileName);
            } catch (storageError) {
              console.error("Storage 파일 삭제 오류:", storageError);
            }
          }

          // 문서는 저장 시 공백 처리 (부모 컴포넌트에서 처리)
        }
        else if (this.mode === "update-artist") {
          // 수정 모드: 파일은 그대로 두고 화면에서만 제거
          // 문서는 저장 시 공백 처리 (부모 컴포넌트에서 처리)
          // 여기서는 아무 작업도 하지 않음
        }

        // 부모 컴포넌트에 삭제 알림
        // ProfileImageUploader는 image-removed를 기대하므로 두 이벤트 모두 emit
        this.$emit("image-removed");

        // 모달 닫기
        this.closeModal();

      } catch (error) {
        console.error(`[${this.mode}] 프로필 사진 삭제 오류:`, error);
        this.$alert("프로필 사진 삭제 중 오류가 발생했습니다.");
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style module>
.modalOverlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000 !important;
  padding: 20px;
  overflow: hidden;
  overscroll-behavior: none;
  touch-action: none;
}

.modalContent {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modalHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modalTitle {
  margin: 0;
  font-size: 1.25rem;
  font-weight: bold;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.closeButton {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.closeButton:hover {
  background: #f8f9fa;
  color: #333;
}

.modalBody {
  padding: 24px;
}

.imagePreviewContainer {
  text-align: center;
  margin-bottom: 24px;
}

.imagePreview {
  width: 120px;
  height: 120px;
  margin: 0 auto 12px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
}

.imagePreviewButton {
  width: 100%;
  height: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.previewImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.noImage {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
}

.imageLabel {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  font-family: "NanumSquareRoundR", sans-serif;
}

.uploadSection {
  text-align: center;
  /* margin-bottom: 24px; */
}

.hiddenInput {
  display: none;
}

.selectButton {
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-bottom: 8px;
}

.selectButton:hover:not(:disabled) {
  background: #4a9eff;
  transform: translateY(-1px);
}

.selectButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.uploadHint {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.uploadProgress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 24px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.uploadProgress p {
  margin: 0;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.modalFooter {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.cancelButton {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.cancelButton:hover:not(:disabled) {
  background: #5a6268;
}

.deleteButton {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deleteButton:hover:not(:disabled) {
  background: #c82333;
}

.saveButton {
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.saveButton:hover:not(:disabled) {
  background: #218838;
}

.cancelButton:disabled,
.deleteButton:disabled,
.saveButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 반응형 디자인 */
@media (max-width: 600px) {
  .modalContent {
    margin: 10px;
    max-height: 95vh;
  }

  .modalHeader,
  .modalBody,
  .modalFooter {
    padding: 16px 20px;
  }

  .imagePreview {
    width: 100px;
    height: 100px;
  }

  .noImage span {
    font-size: 40px;
  }

  .modalFooter {
    flex-direction: column;
    gap: 8px;
  }

  .cancelButton,
  .deleteButton,
  .saveButton {
    width: 100%;
    padding: 12px;
    font-size: 1rem;
  }
}
</style>
